// eslint-disable-next-line wix-editor/no-internal-import
import {LAYOUT} from '@wix/component-protocol/schema'
import type {Builder, PresetData, UnitSizeType} from '@wix/document-services-types'
import type {PresetsWithVariables, PresetVariableData} from '../types'
import {getPresetData} from '../presets'
import type {IPresetsStylesTransformer} from './style-transformer'
import type {CreateExtArgs} from '@wix/document-manager-core'

export interface IPresetsManifestProcessor {
    processPresetsManifest(presetsDataPointers: string[], presets: PresetsWithVariables): Builder.Presets
}

const MIN_SIZE = 1

export class PresetsManifestProcessor implements IPresetsManifestProcessor {
    constructor(
        private readonly createExtArgs: CreateExtArgs,
        private readonly presetsStylesTransformer: IPresetsStylesTransformer
    ) {}

    processPresetsManifest(presetsDataPointers: string[], presets: PresetsWithVariables): Builder.Presets {
        const manifestEntries = presetsDataPointers
            .map(presetId => {
                const presetData = getPresetData(this.createExtArgs, presetId)
                const manifest = this.processPresetManifest(presetId, presets)
                if (!(manifest && presetData)) {
                    return null
                }

                return [presetData.presetId, manifest] as const
            })
            .filter((entry): entry is [string, Builder.PresetItem] => entry !== null)

        return Object.fromEntries(manifestEntries)
    }

    private processPresetManifest(presetId: string, presets: PresetsWithVariables): Builder.PresetItem | null {
        const presetData = getPresetData(this.createExtArgs, presetId)
        const presetVariables: Record<string, PresetVariableData> = presets[presetData.presetId]

        if (!presetData) {
            throw new Error('Preset data not found')
        }
        const {defaultSize, name: displayName} = presetData

        return {
            displayName,
            presetDefaults: {
                cssCustomProperties: this.getDefaultStyles(presetVariables)
            },
            initialSize: this.getInitialSize(defaultSize)
        }
    }

    private getDefaultStyles(presetVariables: Record<string, PresetVariableData>): Builder.StyleOverrides {
        if (!presetVariables) {
            return {}
        }

        return this.presetsStylesTransformer.transformPresetStyles(presetVariables)
    }

    private getInitialSize(defaultSize: PresetData['defaultSize']): Builder.PresetItem['initialSize'] {
        return {
            width: defaultSize?.width && {
                pixels: Math.round(defaultSize.width.value) || MIN_SIZE,
                sizingType: this.getSizingType(defaultSize.width.type)
            },
            height: defaultSize?.height && {
                pixels: Math.round(defaultSize.height.value) || MIN_SIZE,
                sizingType: this.getSizingType(defaultSize.height.type)
            }
        }
    }

    private getSizingType(sizingType: UnitSizeType) {
        switch (sizingType) {
            case 'px':
                return LAYOUT.SIZING_TYPE.pixels
            default:
                return LAYOUT.SIZING_TYPE.UNKNOWN_SizingType
        }
    }
}
